import { DrawerComponent } from 'src/components/DialogComponent';
import { AnswerQuestion } from '../../types/data';
import { FlexCol } from 'src/utils/flexLayouts';
import { CreateDoingForm } from '../formAddAction/FormAddActions';
import React, { useCallback, useState, useMemo, memo } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import _ from 'lodash';
import { setLastAddedId, fetchActionByContextRequest, fetchActionByContextSuccess, clearInconsistenciesByAction, deleteDataVPO } from '../../store/formAddActions/actions';
import { selectLastDocId } from '../../store/formAddActions/selectors';
import { fetchActionsOnlyRequestVPO, toggleAuditRequestVPO, createOrUpdateAuditRequestVPO } from '../../store/data-main/data-main';
import { shallowEqual } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { selectCurrentUser } from 'src/modules/users/selector';

type AddButtonProps = Pick<AnswerQuestion, 'answer' | 'actions' | 'isAudit'> & {
  onClick?: () => void;
  config?: any;
  predefinedValues?: any;
  context?: any;
  answerId?: number | null;
};

const AddButton = memo(function AddButton({ answer, actions, config, onClick, predefinedValues, context, isAudit, answerId }: AddButtonProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const prevUrlRef = React.useRef<string | null>(null);

  const currentUser = useAppSelector(selectCurrentUser);

  const foundActions = useAppSelector(
    (state) => state.vpoStrapi.formAddActions['vpo-actions']?.actionByContext?.data || [],
    shallowEqual
  );

  const lastAddedIdforDelete = useAppSelector((state) => selectLastDocId(state, 'vpo-actions'));

  const canViewArchiveIcon = currentUser?.infoVPO?.zone || currentUser?.infoVPO?.adminRegion;

  const firstActionId = useMemo(() => {
    if (foundActions.length > 0 && foundActions[0]?.linkedVpoAction?.id) {
      return foundActions[0].linkedVpoAction.id;
    }
    return null;
  }, [foundActions]);

  const closeDialog = useCallback(() => {
    setOpen(false);
    setIsNewForm(false);
    setSearchParams({});
    dispatch(clearInconsistenciesByAction());
    if (predefinedValues?.factory && predefinedValues?.column && predefinedValues?.group && context?.currentMonth && context?.currentYear) {
      dispatch(fetchActionsOnlyRequestVPO({
        factory: predefinedValues.factory.id,
        column: predefinedValues.column.id,
        group: predefinedValues.group.id,
        month: context.currentMonth,
        year: context.currentYear,
      }));
    }
  }, [predefinedValues, context, dispatch, setSearchParams]);

  const setOpenDrawer = (value: boolean) => {
    if (open !== value) {
      setOpen(value);

      if (value && predefinedValues) {
        dispatch(clearInconsistenciesByAction());
        dispatch(setLastAddedId('vpo-actions', null));
        dispatch(setLastAddedId('vpo-inconsistencies', null));

        setTimeout(() => {
          setIsNewForm(true);
          dispatch(fetchActionByContextRequest('vpo-actions', predefinedValues));
        }, 200);
      } else {
        setSearchParams({});
        setIsNewForm(true);
      }
    }
  };

  React.useEffect(() => {
    if (open) {
      const actionId = firstActionId || (actions && actions.length > 0 ? actions[0] : null);
      if (actionId) {
        const currentId = searchParams.get('actionCardVPO');
        if (currentId !== String(actionId)) {
          navigate(`?actionCardVPO=${actionId}`, { replace: true });
        }
      }
    }
  }, [open, firstActionId, actions, searchParams, navigate]);

  React.useEffect(() => {
    const urlVpoId = searchParams.get('actionCardVPO');

    // console.log('add-button check:', { urlVpoId, open, actions, firstActionId, match: actions && actions.length > 0 && Number(actions[0]) === Number(urlVpoId) });

    if (urlVpoId && !open && actions && actions.length > 0 && Number(actions[0]) === Number(urlVpoId)) {
      setOpen(true);
      if (predefinedValues) {
        dispatch(fetchActionByContextRequest('vpo-actions', predefinedValues));
      }
    }

  }, [actions]);

  React.useEffect(() => {
    const urlVpoId = searchParams.get('actionCardVPO');

    if (prevUrlRef.current !== null && !urlVpoId && open) {
      setOpen(false);
    }

    prevUrlRef.current = urlVpoId;
  }, [searchParams, open]);

  const handleAuditClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (answerId !== null && answerId !== undefined) {
      dispatch(toggleAuditRequestVPO({ answerId, isAudit: !isAudit }));
    } else {
      if (predefinedValues?.factory && predefinedValues?.subdivision && predefinedValues?.demand) {
        dispatch(createOrUpdateAuditRequestVPO({
          Factory: predefinedValues.factory.id,
          Subdivision: predefinedValues.subdivision.id,
          Requirement: predefinedValues.demand.id,
          Month: context?.currentMonth,
          Year: context?.currentYear,
          Group: predefinedValues.group?.id,
          Column: predefinedValues.column?.id,
          isAudit: true,
        }));
      }
    }
  };
// console.log('actions',actions)
  if (actions) {
    return (
      <>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
          className='group cursor-pointer'
          onClick={() => {
            if (onClick) onClick();
            setOpenDrawer(true);
          }}
        >
          <g clipPath='url(#clip0_2001_1903)'>
            <path
              d='M8.0002 0.450195C3.8252 0.450195 0.450195 3.8252 0.450195 8.0002C0.450195 12.1752 3.8252 15.5752 8.0002 15.5752C12.1752 15.5752 15.5752 12.1752 15.5752 8.0002C15.5752 3.8252 12.1752 0.450195 8.0002 0.450195ZM8.0002 14.4502C4.4502 14.4502 1.5752 11.5502 1.5752 8.0002C1.5752 4.4502 4.4502 1.5752 8.0002 1.5752C11.5502 1.5752 14.4502 4.47519 14.4502 8.0252C14.4502 11.5502 11.5502 14.4502 8.0002 14.4502Z'
              fill='#2D68F8'
              className='group-hover:hidden'
            />
            <path
              d='M10.1497 5.67533L7.17475 8.57533L5.82475 7.25033C5.59975 7.02533 5.24975 7.05033 5.02475 7.25033C4.79975 7.47533 4.82475 7.82533 5.02475 8.05033L6.62475 9.60033C6.77475 9.75033 6.97475 9.82533 7.17475 9.82533C7.37475 9.82533 7.57475 9.75033 7.72475 9.60033L10.9497 6.50033C11.1747 6.27533 11.1747 5.92533 10.9497 5.70033C10.7247 5.47533 10.3747 5.47533 10.1497 5.67533Z'
              fill='#2D68F8'
              className='group-hover:hidden'
            />

            <path
              xmlns='http://www.w3.org/2000/svg'
              fillRule='evenodd'
              clipRule='evenodd'
              d='M0.450195 8.0002C0.450195 3.8252 3.8252 0.450195 8.0002 0.450195C12.1752 0.450195 15.5752 3.8252 15.5752 8.0002C15.5752 12.1752 12.1752 15.5752 8.0002 15.5752C3.8252 15.5752 0.450195 12.1752 0.450195 8.0002ZM7.17524 8.57517L10.1502 5.67517C10.3752 5.47517 10.7252 5.47517 10.9502 5.70017C11.1752 5.92517 11.1752 6.27517 10.9502 6.50017L7.72523 9.60017C7.57524 9.75017 7.37524 9.82517 7.17524 9.82517C6.97524 9.82517 6.77524 9.75017 6.62524 9.60017L5.02524 8.05017C4.82524 7.82517 4.80024 7.47517 5.02524 7.25017C5.25024 7.05017 5.60023 7.02517 5.82524 7.25017L7.17524 8.57517Z'
              fill='#2D68F8'
              className='hidden group-hover:block'
            />
          </g>
          <defs>
            <clipPath id='clip0_2001_1903'>
              <rect width='16' height='16' fill='white' />
            </clipPath>
          </defs>
        </svg>
        {canViewArchiveIcon && (
          <svg
            width='10'
            height='15'
            viewBox='0 0 10 15'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='group cursor-pointer'
            onClick={handleAuditClick}
          >
            <path
              d='M8.6665 5.99996V1.73329C8.6665 1.35992 8.6665 1.17324 8.59384 1.03063C8.52993 0.905191 8.42794 0.803204 8.3025 0.739288C8.15989 0.666626 7.97321 0.666626 7.59984 0.666626H1.73317C1.3598 0.666626 1.17312 0.666626 1.03051 0.739288C0.905069 0.803204 0.803082 0.905191 0.739166 1.03063C0.666504 1.17324 0.666504 1.35992 0.666504 1.73329V5.99996M8.6665 5.99996H0.666504M8.6665 5.99996V6.13329C8.6665 7.2534 8.6665 7.81345 8.44852 8.24127C8.25677 8.6176 7.95081 8.92356 7.57449 9.11531C7.14666 9.33329 6.58661 9.33329 5.4665 9.33329H3.8665C2.7464 9.33329 2.18635 9.33329 1.75852 9.11531C1.3822 8.92356 1.07624 8.6176 0.884491 8.24127C0.666504 7.81345 0.666504 7.2534 0.666504 6.13329V5.99996M6.33317 9.33329V12.3333C6.33317 13.2538 5.58698 14 4.6665 14C3.74603 14 2.99984 13.2538 2.99984 12.3333V9.33329'
              stroke='#9CA3AF'
              strokeWidth='1.33333'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        )}
        <DrawerComponent open={open} zIndex={50} percentage={'87'} toggleDrawer={closeDialog}>
          <FlexCol className='p-10 shrink'>
            <h1 className='mb-8 text-black'>Действия</h1>
            <CreateDoingForm
              key={`form-${firstActionId || 'new'}`}
              toggleDoing={setOpenDrawer}
              predefinedValues={predefinedValues}
              context={context}
              firstActionId={firstActionId}
              resetSelect={false}
              onActionAdded={() => {
                closeDialog();
              }}
            />
          </FlexCol>
        </DrawerComponent>
      </>
    );
  }
  return (
    <>
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 16 16'
        fill='none'
        className='group cursor-pointer'
        onClick={() => {
          setOpenDrawer(true);
        }}
      >
        <g clipPath='url(#clip0_2001_28231)'>
          <path
            d='M10.5504 7.42539L8.55039 7.42539V5.45039C8.55039 5.15039 8.30039 4.90039 7.97539 4.90039C7.67539 4.90039 7.42539 5.15039 7.42539 5.47539V7.45039H5.45039C5.15039 7.45039 4.90039 7.70039 4.90039 8.02539C4.90039 8.32539 5.15039 8.57539 5.47539 8.57539H7.45039L7.45039 10.5504C7.45039 10.8504 7.70039 11.1004 8.02539 11.1004C8.32539 11.1004 8.57539 10.8504 8.57539 10.5254V8.55039H10.5504C10.8504 8.55039 11.1004 8.30039 11.1004 7.97539C11.1004 7.67539 10.8504 7.42539 10.5504 7.42539Z'
            fill={answer === 'Нет' ? '#F56060' : '#9CA3AF'}
            className='group-hover:hidden'
          />
          <path
            d='M8.0002 0.450195C3.8252 0.450195 0.450195 3.8252 0.450195 8.0002C0.450195 12.1752 3.85019 15.5752 8.0252 15.5752C12.2002 15.5752 15.6002 12.1752 15.6002 8.0002C15.5752 3.8252 12.1752 0.450195 8.0002 0.450195ZM8.0002 14.4502C4.4502 14.4502 1.5752 11.5502 1.5752 8.0002C1.5752 4.4502 4.4502 1.5752 8.0002 1.5752C11.5502 1.5752 14.4502 4.4502 14.4502 8.0002C14.4502 11.5502 11.5502 14.4502 8.0002 14.4502Z'
            fill={answer === 'Нет' ? '#F56060' : '#9CA3AF'}
            className='group-hover:hidden'
          />
          <path
            xmlns='http://www.w3.org/2000/svg'
            fillRule='evenodd'
            clipRule='evenodd'
            d='M0.450195 8.0002C0.450195 3.8252 3.8252 0.450195 8.0002 0.450195C12.1752 0.450195 15.5752 3.8252 15.6002 8.0002C15.6002 12.1752 12.2002 15.5752 8.0252 15.5752C3.85019 15.5752 0.450195 12.1752 0.450195 8.0002ZM8.55023 7.42523H10.5502C10.8502 7.42523 11.1002 7.67523 11.1002 7.97523C11.1002 8.30023 10.8502 8.55023 10.5502 8.55023H8.57523V10.5252C8.57523 10.8502 8.32523 11.1002 8.02523 11.1002C7.70023 11.1002 7.45023 10.8502 7.45023 10.5502V8.57523H5.47523C5.15023 8.57523 4.90023 8.32523 4.90023 8.02523C4.90023 7.70023 5.15023 7.45023 5.45023 7.45023H7.42523V5.47523C7.42523 5.15023 7.67523 4.90023 7.97523 4.90023C8.30023 4.90023 8.55023 5.15023 8.55023 5.45023V7.42523Z'
            fill={answer === 'Нет' ? '#F56060' : '#9CA3AF'}
            className='hidden group-hover:block'
          />
        </g>
        <defs>
          <clipPath id='clip0_2001_28231'>
            <rect width='16' height='16' fill='white' />
          </clipPath>
        </defs>
      </svg>
      {canViewArchiveIcon && (
        <svg
          width='10'
          height='15'
          viewBox='0 0 10 15'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          className='group cursor-pointer'
          onClick={handleAuditClick}
        >
          <path
            d='M8.6665 5.99996V1.73329C8.6665 1.35992 8.6665 1.17324 8.59384 1.03063C8.52993 0.905191 8.42794 0.803204 8.3025 0.739288C8.15989 0.666626 7.97321 0.666626 7.59984 0.666626H1.73317C1.3598 0.666626 1.17312 0.666626 1.03051 0.739288C0.905069 0.803204 0.803082 0.905191 0.739166 1.03063C0.666504 1.17324 0.666504 1.35992 0.666504 1.73329V5.99996M8.6665 5.99996H0.666504M8.6665 5.99996V6.13329C8.6665 7.2534 8.6665 7.81345 8.44852 8.24127C8.25677 8.6176 7.95081 8.92356 7.57449 9.11531C7.14666 9.33329 6.58661 9.33329 5.4665 9.33329H3.8665C2.7464 9.33329 2.18635 9.33329 1.75852 9.11531C1.3822 8.92356 1.07624 8.6176 0.884491 8.24127C0.666504 7.81345 0.666504 7.2534 0.666504 6.13329V5.99996M6.33317 9.33329V12.3333C6.33317 13.2538 5.58698 14 4.6665 14C3.74603 14 2.99984 13.2538 2.99984 12.3333V9.33329'
            stroke='#9CA3AF'
            strokeWidth='1.33333'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      )}
      <DrawerComponent open={open} zIndex={50} percentage={'87'} toggleDrawer={closeDialog}>
        <FlexCol className='p-10 shrink'>
          <h1 className='mb-8 text-black'>Действия</h1>
          <CreateDoingForm
            key={`form-${firstActionId || 'new'}`}
            toggleDoing={setOpenDrawer}
            predefinedValues={predefinedValues}
            context={context}
            isNewForm={isNewForm}
            isLoading={true}
            firstActionId={firstActionId}
            resetSelect={false}
            onActionAdded={() => {
              closeDialog();
            }}
          />
        </FlexCol>
      </DrawerComponent>
    </>
  );
});

export default AddButton;
